import './ChatPage.scss';

import { useState, useEffect, useRef, useCallback } from 'react';
import { api, streamPost } from '../../utils/api';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import JobCard from './JobCard';
import { useCurrentUser } from "../../UserProvider";

import SpotifyConnect from '../OnboardingPage/OnboardingSteps/SpotifyConnect';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faCircleUser } from "@fortawesome/free-solid-svg-icons";

export default function ChatPage() {
  const { currentUser } = useCurrentUser();

  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const bottomRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const processFinalMessage = useCallback((text) => {
    const parts = [];
    const regex = /(PJR::(\d+)::PJR)|(PSC::SPOTIFY_CONNECT::PSC)/g;

    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const fullMatch = match[0];
      const pjrId = match[2];
      const isSpotify = fullMatch === 'PSC::SPOTIFY_CONNECT::PSC';

      if (match.index > lastIndex) {
        parts.push({
          type: 'markdown',
          content: text.slice(lastIndex, match.index),
        });
      }

      if (pjrId) {
        parts.push({
          type: 'pjr',
          id: pjrId,
        });
      }

      if (isSpotify) {
        parts.push({
          type: 'spotify',
        });
      }

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push({
        type: 'markdown',
        content: text.slice(lastIndex),
      });
    }

    return parts;
  }, []);

  const renderFinalMessage = useCallback((parts) => {
    return parts.map((part, i) => {
      if (part.type === 'markdown') {
        return <ReactMarkdown key={i}>{part.content}</ReactMarkdown>;
      }

      if (part.type === 'pjr') {
        return <JobCard key={i} jobId={part.id} afterRender={scrollToBottom} />;
      }

      if (part.type === 'spotify') {
        return <SpotifyConnect key={i} handleNext={scrollToBottom} />;
      }

      return null;
    });
  }, [scrollToBottom]);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await api.get(`/messages`);

        const hydrated = response.data.map((msg) => ({
          ...msg,
          finalized: true,
          finalizedContent: renderFinalMessage(processFinalMessage(msg.content)),
        }));

        setMessages(hydrated);
      } catch (err) {
        console.log('error');
      }
    };

    fetchMessages();
  }, [renderFinalMessage, processFinalMessage]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      content: message,
      sender: 'human',
      finalized: true,
      finalizedContent: renderFinalMessage(processFinalMessage(message)),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage('');

    const botMessage = {
      id: Date.now() + 1,
      content: '',
      sender: 'assistant',
      finalized: false,
      finalizedContent: null,
    };

    setMessages((prev) => [...prev, botMessage]);

    const updateBotMessage = (newContent, finalized = false) => {
      setMessages((prev) => {
        const updated = [...prev];
        const botIndex = updated.findIndex((m) => m.id === botMessage.id);

        if (botIndex !== -1) {
          updated[botIndex] = {
            ...updated[botIndex],
            content: finalized ? null : newContent,
            finalized,
            finalizedContent: finalized
              ? renderFinalMessage(processFinalMessage(newContent))
              : null,
          };
        }

        return updated;
      });
    };

    try {
      const reader = await streamPost(`/messages/stream`, {
        content: userMessage.content,
      });

      const decoder = new TextDecoder();
      let done = false;
      let accumulated = '';

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;

        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          accumulated += chunk;
          updateBotMessage(accumulated, false);
        }
      }

      updateBotMessage(accumulated, true);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="__chat">
      <div className="content">
        <div className="welcome">Ask Papaya!</div>

        <div className="messages">
          {messages.map((message, index) => (
            <>
              <div key={index} className={classNames('message', message.sender)}>
                {message.finalized && message.sender === 'assistant' ? (
                  message.finalizedContent
                ) : (
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                )}
              </div>
              {message.sender === 'human' &&
                (currentUser?.profile?.avatar_url ? (
                  <img
                    src={currentUser.profile.avatar_url}
                    alt="User avatar"
                    className="human__avatar"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <FontAwesomeIcon icon={faCircleUser} className="human__avatar" />
                ))
              }
              {message.sender === 'assistant' &&
                <img src="/images/papayas.svg" alt="Papaya" className="assistant__avatar" />
              }
            </>
          ))}
        </div>

        <div ref={bottomRef} />
      </div>

      <div className="send-menu">
        <textarea
          type="text"
          placeholder="Ask Anything"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
        />
        <button className="send" onClick={handleSendMessage}>
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      </div>
    </div>
  );
}
